import { merge } from 'bernie-core';

// Bernie
import { ILocalization } from '@shared-ui/localization-context';

import { PDP_CHECKIN_QUERY_PARAM, PDP_CHECKOUT_QUERY_PARAM, PDP_DATELESS_QUERY_PARAM } from '../../constants/dates';

import { IRoomQueryAdapter, queryObject } from './adapters';

const { withoutKeys } = merge;

const CHILD_UNDERSCORE_REGEX = /\d+_(\d+)/i;

const rooms: IRoomQueryAdapter = {
  parseInRoomParams: (query: any): any => {
    const MAX_ROOM_PARAMS = 10;
    const roomParams: any[] = [];
    const roomsCollection: any[] = [];
    const adultsRegex = /a(\d+)/i;
    const childrenRegex = /c(\d+)/i;

    /*
     sample query object:

     {
       rm1: 'a2:c4:c0:c5',
       rm2: 'a1',
       foo: 'bar'
     }
     */

    // extract valid rooms from the query object
    for (let index = 1, key = '', value = ''; index <= MAX_ROOM_PARAMS; index++) {
      key = `rm${index}`;

      if (Object.prototype.hasOwnProperty.call(query, key)) {
        value = query[key];

        if (typeof value === 'string' && value.length > 0) {
          roomParams.push(query[key]);

          delete query[key];
        }

        continue;
      }

      break;
    }

    // cycle through the rooms and parseIn their content
    roomParams.forEach((room: string) => {
      if (adultsRegex.test(room)) {
        const adults = parseInt(adultsRegex.exec(room)?.[1] || '', 10) || 0;
        const children: any[] = [];
        const roomParts = room.split(':');

        roomParts.forEach((part: any) => {
          if (childrenRegex.test(part)) {
            children.push({
              age: parseInt(childrenRegex.exec(part)?.[1] || '', 10) || 0,
            });
          }
        });

        if (adults) {
          roomsCollection.push({
            adults,
            children,
          });
        }
      }
    });

    query.rooms = roomsCollection;

    return query;
  },

  parseInGuestParams: (query: any): any => {
    /*
    sample guest query object:
      {
        adults: '2,1',
        children: '3,0',
        childAge: '4,0,5'
      }
    */

    /*
    sample guest query object for vrbex:
      {
        adults: '2',
        children: '1_3',
      }
    */

    const roomsCollection: any[] = [];
    const adultsArray = getParameterArray(query, 'adults');
    const childrenArray = getParameterArray(query, 'children');
    const childAgeArray = getParameterArray(query, 'childAge');

    if (isChildrenParametersUnderscored(childrenArray)) {
      const underscoredChildParams = getUnderscoredChildParams(adultsArray, childrenArray);
      if (underscoredChildParams) {
        roomsCollection.push(underscoredChildParams);
      }
    } else {
      // bex logic
      adultsArray.forEach((adult, i) => {
        const adults = parseInt(adult, 10) || 0;
        const children: any[] = [];
        const childCount = (childrenArray && childrenArray[i] && parseInt(childrenArray[i], 10)) || 0;
        const childAges = (childAgeArray && childAgeArray.splice(0, childCount)) || [];

        childAges.forEach(childAge => {
          children.push({
            age: parseInt(childAge, 10) || 0,
          });
        });

        if (adults) {
          roomsCollection.push({
            adults,
            children,
          });
        }
      });
    }

    query.rooms = roomsCollection;

    delete query.adults;
    delete query.children;
    delete query.childAge;

    return query;
  },

  parseIn: (query: any): any => {
    query = queryObject(query);

    const hasRoomParams = query && query.rm1 !== undefined;
    if (hasRoomParams) {
      return parseInRoomParams(query);
    }

    return parseInGuestParams(query);
  },

  parseOut: (query: any): any => {
    query = queryObject(query);

    const roomsCollection = query && query.rooms;
    let count = 1;

    if (typeof roomsCollection === 'object' && roomsCollection && roomsCollection.length > 0) {
      roomsCollection.forEach((room: any) => {
        if (room.adults > 0) {
          const roomStrings = [`a${room.adults}`];

          if (room.children && room.children.length > 0) {
            room.children.forEach((child: any) => {
              roomStrings.push(`c${child.age || 0}`);
            });
          }

          query[`rm${count}`] = roomStrings.join(':');
          count += 1;
        }
      });

      delete query.rooms;
    }

    return query;
  },
};

function parseIn(query: any): any {
  const startDate = query[PDP_CHECKIN_QUERY_PARAM] || query.startDate || null;
  const endDate = query[PDP_CHECKOUT_QUERY_PARAM] || query.endDate || null;

  const dateFields = {};

  if (startDate) {
    // @ts-ignore
    dateFields.startDate = startDate;
  }

  if (endDate) {
    // @ts-ignore
    dateFields.endDate = endDate;
  }

  return withoutKeys([PDP_CHECKIN_QUERY_PARAM, PDP_CHECKOUT_QUERY_PARAM]).merge({}, rooms.parseIn(query), dateFields);
}

function parseOut(query: any, l10n: ILocalization): any {
  if (!query) {
    return {};
  }

  const checkInDate = parseDate(query.startDate, query.localDateFormat, l10n) ?? '';
  const checkOutDate = parseDate(query.endDate, query.localDateFormat, l10n) ?? '';

  const dates: any = {};

  if (checkInDate || checkOutDate) {
    dates[PDP_DATELESS_QUERY_PARAM] = undefined;
  } else {
    dates[PDP_DATELESS_QUERY_PARAM] = true;
  }

  dates[PDP_CHECKIN_QUERY_PARAM] = checkInDate;
  dates[PDP_CHECKOUT_QUERY_PARAM] = checkOutDate;

  return {
    ...withoutKeys(['localDateFormat', 'top_dp']).merge({}, rooms.parseOut(query, l10n), dates),
    startDate: dates[PDP_DATELESS_QUERY_PARAM] ? '' : checkInDate,
    endDate: dates[PDP_DATELESS_QUERY_PARAM] ? '' : checkOutDate,
  };
}

function parseInRoomParams(query: any): any {
  return rooms.parseInRoomParams(query);
}

function parseInGuestParams(query: any): any {
  return rooms.parseInGuestParams(query);
}

function parseDate(date = '', outputFormat = 'yyyy-MM-dd', l10n: ILocalization): string {
  try {
    const parsedDate = l10n.parseLocalDateString(date, { raw: 'yyyy-MM-dd' }) as Date;

    if (parsedDate) {
      return l10n.formatDate(parsedDate, { raw: outputFormat });
    }
  } catch (error) {
    return date;
  }

  return '';
}

function getParameterArray(query: any, parameter: string): string[] {
  const paramterValue = query && query[parameter];

  return (paramterValue && typeof paramterValue === 'string' && paramterValue.split(',')) || [];
}

function isChildrenParametersUnderscored(childrenArray: string[]): boolean {
  return childrenArray.length > 0 && CHILD_UNDERSCORE_REGEX.test(childrenArray[0]);
}

function getUnderscoredChildParams(adultsArray: string[], childrenArray: string[]) {
  // vrbex logic
  const adults = adultsArray.reduce((count, adult) => count + (parseInt(adult, 10) || 0));
  const children = adults
    ? childrenArray.map(child => {
        const childAge = parseInt(CHILD_UNDERSCORE_REGEX.exec(child)?.[1] || '', 10) || 0;

        return {
          age: childAge,
        };
      })
    : {};

  if (adults) {
    return {
      adults: parseInt(adults, 10),
      children,
    };
  }
}

export const InfositeQueryAdapter: IRoomQueryAdapter = {
  parseIn,
  parseOut,
  parseInRoomParams,
  parseInGuestParams,
};
